import React, { useMemo, useState } from "react";
import { Card, Empty, Layout, Menu, Space, Typography } from "antd";
import { useParams } from "react-router-dom";
import { SETTINGS_SECTIONS } from "./sections/SectionContract";

const { Sider, Content } = Layout;
const { Title, Paragraph, Text } = Typography;

interface SettingsShellProps {
    // Optional pre-selected section key — wired by deep links once individual
    // section panels land. Falls back to the first section in the nav order.
    initialSectionKey?: string;
}

// Shell that owns the per-client Settings page chrome: left-rail nav across the
// 13 sections (defined in SectionContract) plus a content slot that renders the
// active section's Panel. The Panels themselves land per-section in follow-up
// tickets (FORT-119+); this commit ships the layout and a placeholder slot.
//
// The shell is mounted at /fortius/clients/:clientId/settings — we read the
// route param so the header surfaces which tenant is being edited (operators
// jumping between tenants would otherwise see indistinguishable placeholder
// pages) and so e2e/visual tests can assert the right tenant is rendered via
// the data-client-id attribute.
export const SettingsShell: React.FC<SettingsShellProps> = ({ initialSectionKey }) => {
    const { clientId } = useParams<{ clientId: string }>();

    const navItems = useMemo(
        () => SETTINGS_SECTIONS
            .filter((s) => !s.wizardOnly)
            .map((s) => ({ key: s.key, label: s.label })),
        []
    );

    const [activeKey, setActiveKey] = useState<string>(
        initialSectionKey ?? navItems[0]?.key ?? 'general'
    );

    const activeSection = SETTINGS_SECTIONS.find((s) => s.key === activeKey);

    return (
        <Layout
            data-testid="settings-shell"
            data-client-id={clientId ?? ''}
            style={{ background: 'transparent' }}
        >
            <Sider
                width={240}
                theme="light"
                style={{ background: 'transparent' }}
                data-testid="settings-shell-nav"
            >
                <Menu
                    mode="inline"
                    selectedKeys={[activeKey]}
                    onSelect={({ key }) => setActiveKey(key as string)}
                    items={navItems}
                    style={{ borderInlineEnd: 'none' }}
                />
            </Sider>
            <Content style={{ paddingLeft: 24 }}>
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <Space direction="vertical" size={0} data-testid="settings-shell-header">
                        <Title level={3} style={{ marginBottom: 0 }}>Client settings</Title>
                        <Text type="secondary" data-testid="settings-shell-client-id">
                            Tenant id: {clientId ?? '(unknown)'}
                        </Text>
                    </Space>
                    <Card data-testid={`settings-shell-panel-${activeKey}`}>
                        <Title level={4}>{activeSection?.label}</Title>
                    {activeSection?.Panel ? (
                        <activeSection.Panel
                            value={undefined as unknown}
                            onChange={() => {
                                /* shells panels manage their own value once landed */
                            }}
                        />
                    ) : (
                        <Empty
                            description={
                                <Paragraph type="secondary">
                                    The {activeSection?.label} section will be wired up in a follow-up ticket.
                                </Paragraph>
                            }
                        />
                    )}
                    </Card>
                </Space>
            </Content>
        </Layout>
    );
};
